import type {
  BuiltForm,
  ConstructionAgeBand,
  HeatingSystem,
  PropertyType,
  WallConstruction,
  WallInsulation,
  WindowGlazingType,
  OccupancyArchetype,
} from '@/gql/graphql';

type SelectOption = { label: string; value: string };

// Each option array uses `satisfies` to ensure values are valid enum members.
// If a GQL enum value is added/removed, these will produce a type error.
// The `VALUES` arrays are exported for reuse in Zod schemas.

export const PROPERTY_TYPE_VALUES = [
  'HOUSE',
  'BUNGALOW',
  'FLAT',
  'MAISONETTE',
  'PARK_HOME',
] as const satisfies readonly PropertyType[];

export const PROPERTY_TYPE_OPTIONS = [
  { label: 'House', value: 'HOUSE' },
  { label: 'Bungalow', value: 'BUNGALOW' },
  { label: 'Flat', value: 'FLAT' },
  { label: 'Maisonette', value: 'MAISONETTE' },
  { label: 'Park Home', value: 'PARK_HOME' },
] satisfies { label: string; value: PropertyType }[] as SelectOption[];

export const BUILT_FORM_VALUES = [
  'DETACHED',
  'SEMI_DETACHED',
  'MID_TERRACE',
  'END_TERRACE',
  'ENCLOSED_MID_TERRACE',
  'ENCLOSED_END_TERRACE',
] as const satisfies readonly BuiltForm[];

export const BUILT_FORM_OPTIONS = [
  { label: 'Detached', value: 'DETACHED' },
  { label: 'Semi-Detached', value: 'SEMI_DETACHED' },
  { label: 'Mid-Terrace', value: 'MID_TERRACE' },
  { label: 'End-Terrace', value: 'END_TERRACE' },
  { label: 'Enclosed Mid-Terrace', value: 'ENCLOSED_MID_TERRACE' },
  { label: 'Enclosed End-Terrace', value: 'ENCLOSED_END_TERRACE' },
] satisfies { label: string; value: BuiltForm }[] as SelectOption[];

export const CONSTRUCTION_AGE_BAND_VALUES = [
  'PRE_1930',
  '_1930_1975',
  '_1976_1995',
  '_1996_2006',
  '_2007_2012',
  '_2013_PRESENT',
] as const satisfies readonly ConstructionAgeBand[];

export const CONSTRUCTION_AGE_BAND_OPTIONS = [
  { label: 'Pre 1930', value: 'PRE_1930' },
  { label: '1930–1975', value: '_1930_1975' },
  { label: '1976–1995', value: '_1976_1995' },
  { label: '1996–2006', value: '_1996_2006' },
  { label: '2007–2012', value: '_2007_2012' },
  { label: '2013–present', value: '_2013_PRESENT' },
] satisfies { label: string; value: ConstructionAgeBand }[] as SelectOption[];

export const OCCUPANCY_ARCHETYPE_VALUES = [
  'HOME_ALL_DAY',
  'IN_HALF_THE_DAY',
  'OUT_DURING_THE_DAY',
] as const satisfies readonly OccupancyArchetype[];

export const OCCUPANCY_ARCHETYPE_OPTIONS = [
  { label: 'Home all day', value: 'HOME_ALL_DAY' },
  { label: 'In half the day', value: 'IN_HALF_THE_DAY' },
  { label: 'Out all day', value: 'OUT_DURING_THE_DAY' },
] satisfies { label: string; value: OccupancyArchetype }[] as SelectOption[];

export const HEATING_SYSTEM_VALUES = [
  'GAS',
  'OIL',
  'LPG',
  'ELECTRIC',
] as const satisfies readonly HeatingSystem[];

export const HEATING_SYSTEM_OPTIONS = [
  { label: 'Gas', value: 'GAS' },
  { label: 'Oil', value: 'OIL' },
  { label: 'LPG', value: 'LPG' },
  { label: 'Electric', value: 'ELECTRIC' },
] satisfies { label: string; value: HeatingSystem }[] as SelectOption[];

export const WALL_CONSTRUCTION_VALUES = [
  'CAVITY',
  'SOLID_BRICK',
  'SOLID_STONE',
  'SOLID_CONCRETE',
  'TIMBER',
] as const satisfies readonly WallConstruction[];

export const WALL_CONSTRUCTION_OPTIONS = [
  { label: 'Cavity', value: 'CAVITY' },
  { label: 'Solid Brick', value: 'SOLID_BRICK' },
  { label: 'Solid Stone', value: 'SOLID_STONE' },
  { label: 'Solid Concrete', value: 'SOLID_CONCRETE' },
  { label: 'Timber', value: 'TIMBER' },
] satisfies { label: string; value: WallConstruction }[] as SelectOption[];

export const WALL_INSULATION_VALUES = [
  'FILLED',
  'UNFILLED',
  'INTERNAL',
  'EXTERNAL',
  'UNINSULATED',
] as const satisfies readonly WallInsulation[];

const CAVITY_INSULATION_OPTIONS = [
  { label: 'Filled', value: 'FILLED' },
  { label: 'Unfilled', value: 'UNFILLED' },
] satisfies {
  label: string;
  value: Extract<WallInsulation, 'FILLED' | 'UNFILLED'>;
}[] as SelectOption[];

const SOLID_INSULATION_OPTIONS = [
  { label: 'Internal', value: 'INTERNAL' },
  { label: 'External', value: 'EXTERNAL' },
  { label: 'Uninsulated', value: 'UNINSULATED' },
] satisfies {
  label: string;
  value: Extract<WallInsulation, 'INTERNAL' | 'EXTERNAL' | 'UNINSULATED'>;
}[] as SelectOption[];

export const WINDOW_GLAZING_VALUES = [
  'SINGLE',
  'DOUBLE',
  'TRIPLE',
] as const satisfies readonly WindowGlazingType[];

export const WINDOW_GLAZING_OPTIONS = [
  { label: 'Single', value: 'SINGLE' },
  { label: 'Double', value: 'DOUBLE' },
  { label: 'Triple', value: 'TRIPLE' },
] satisfies { label: string; value: WindowGlazingType }[] as SelectOption[];

export const getWallInsulationOptions = (
  wallConstruction: string | undefined
): SelectOption[] => {
  if (wallConstruction === 'CAVITY') return [...CAVITY_INSULATION_OPTIONS];
  if (isSolidConstruction(wallConstruction)) {
    return [...SOLID_INSULATION_OPTIONS];
  }
  return [...CAVITY_INSULATION_OPTIONS, ...SOLID_INSULATION_OPTIONS];
};

const isCavityConstruction = (value: string | undefined): value is 'CAVITY' =>
  value === 'CAVITY';

const isSolidConstruction = (
  value: string | undefined
): value is 'SOLID_BRICK' | 'SOLID_STONE' | 'SOLID_CONCRETE' | 'TIMBER' =>
  value === 'SOLID_BRICK' ||
  value === 'SOLID_STONE' ||
  value === 'SOLID_CONCRETE' ||
  value === 'TIMBER';

export const isValidInsulationForConstruction = (
  insulation: string | undefined,
  construction: string | undefined
): boolean => {
  if (!insulation || !construction) return true;
  if (isCavityConstruction(construction)) {
    return CAVITY_INSULATION_OPTIONS.some((o) => o.value === insulation);
  }
  if (isSolidConstruction(construction)) {
    return SOLID_INSULATION_OPTIONS.some((o) => o.value === insulation);
  }
  return true;
};
